import React, { useEffect, useState } from 'react';
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import * as gcomponentItemActions from '../../store/gcomponentItem';

/** 자재품종에 넣은 칸 순서. 화면의 제품·규격 순서와 다르다. */
export const materialSlotLookupCode = (values) => (
  (values || []).map((row) => row?.code).filter(Boolean).join('|')
);

/** 유리 구성과 같이 한 줄을 3칸으로 나눈다. */
const slotColumnSx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  columnGap: 2,
  rowGap: 2,
};

const isThicknessDetail = (detail) => {
  const code = String(detail?.gcomponentCode || '').toUpperCase();
  const name = String(detail?.gcomponentName || '');
  return code.includes('THICKNESS') || name.includes('두께');
};

/** 인증규격 옆 제품 목록과 같은 칸. 필름은 FILM TYPE이고 항목 applyType이 productName이다. */
const isProductDetail = (detail, items) => {
  if (isThicknessDetail(detail)) return false;
  const code = String(detail?.gcomponentCode || '').toUpperCase();
  const name = String(detail?.gcomponentName || '');
  if (name.includes('제품') || code.includes('PRODUCT') || code === 'FILM TYPE') return true;
  const typed = (items || []).map((item) => item?.applyType).filter(Boolean);
  return typed.length > 0 && typed.every((applyType) => applyType === 'productName');
};

const orderedDetails = (slot) => [...(slot?.details || [])].sort((a, b) => (
  (a?.order ?? 0) - (b?.order ?? 0)
));

/** 화면은 제품 다음 규격. 저장 배열은 자재품종 순서를 유지한다. */
const displayDetails = (details, omitProduct, itemsByComponent) => {
  const products = [];
  const specs = [];
  details.forEach((detail) => {
    if (isProductDetail(detail, itemsByComponent?.[detail.gcomponentId])) products.push(detail);
    else specs.push(detail);
  });
  return omitProduct ? specs : [...products, ...specs];
};

/** 프로젝트에서 이미 고른 제품명·두께. 폴더 목록을 다시 고르지 않는다. */
export const projectSlotRows = (slot, { productName = '', thickness = '' } = {}) => {
  const name = String(productName || '').trim();
  const thick = String(thickness || '').trim();
  return orderedDetails(slot).map((detail) => {
    if (isProductDetail(detail) && name) {
      return {
        gcomponentId: detail.gcomponentId,
        gcomponentCode: detail.gcomponentCode || '',
        code: name,
        name,
      };
    }
    const thicknessDetail = isThicknessDetail(detail);
    if (thicknessDetail && thick) {
      return {
        gcomponentId: detail.gcomponentId,
        gcomponentCode: detail.gcomponentCode || '',
        code: thick,
        name: thick,
      };
    }
    return null;
  }).filter(Boolean);
};

const SubMatFolderMaterialSlotFields = ({
  slot,
  value,
  onChange,
  lockedProductName = '',
  readOnly = false,
  omitProduct = false,
  inline = false,
  fieldWidth = 260,
}) => {
  const [itemsByComponent, setItemsByComponent] = useState({});
  const ids = (slot?.details || []).map((detail) => detail.gcomponentId).filter(Boolean).join('|');

  useEffect(() => {
    const componentIds = ids ? ids.split('|') : [];
    if (!componentIds.length) {
      setItemsByComponent({});
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const next = {};
      await Promise.all(componentIds.map(async (id) => {
        try {
          next[id] = await gcomponentItemActions.selectAllByGcomponentIdDirect(id) || [];
        } catch (_err) {
          next[id] = [];
        }
      }));
      if (!cancelled) setItemsByComponent(next);
    })();
    return () => { cancelled = true; };
  }, [slot?.id, ids]);

  const details = orderedDetails(slot);
  const shownDetails = displayDetails(details, omitProduct, itemsByComponent);
  const productName = String(lockedProductName || '').trim();

  const selectedOf = (detail) => {
    const rows = value || [];
    return rows.find((row) => row.gcomponentId === detail.gcomponentId)
      || rows.find((row) => row.code && !row.gcomponentId && (
        String(detail.gcomponentCode || '').includes('THICKNESS')
        || String(detail.gcomponentName || '').includes('두께')
      ))
      || null;
  };

  const handleChange = (detail, itemId) => {
    const items = itemsByComponent[detail.gcomponentId] || [];
    const item = items.find((row) => row.id === itemId) || null;
    const next = details.map((row) => {
      if (row.gcomponentId === detail.gcomponentId) {
        if (!item) return null;
        return {
          gcomponentId: detail.gcomponentId,
          gcomponentCode: detail.gcomponentCode || '',
          itemId: item.id,
          code: item.code || '',
          name: item.name || '',
        };
      }
      return selectedOf(row);
    }).filter(Boolean);
    onChange?.(next);
  };

  if (!shownDetails.length) return null;

  const fields = shownDetails.map((detail) => {
    const items = itemsByComponent[detail.gcomponentId] || [];
    const locked = isProductDetail(detail, items) && Boolean(productName);
    const lockedItem = locked
      ? items.find((item) => item.name === productName || item.code === productName)
      : null;
    const selected = selectedOf(detail);
    const catalogMatch = items.find((item) => item.id === selected?.itemId)
      || items.find((item) => item.code && item.code === selected?.code);
    const projectOnly = readOnly && selected?.code && !catalogMatch
      ? { id: `__project__${selected.code}`, name: selected.name || selected.code, code: selected.code }
      : null;
    const shownItems = locked && productName && !lockedItem
      ? [{ id: '__locked_product__', name: productName, code: productName }, ...items]
      : (projectOnly ? [projectOnly, ...items] : items);
    const current = locked
      ? (lockedItem?.id || '__locked_product__')
      : (catalogMatch?.id || projectOnly?.id || '');
    const label = detail.gcomponentName || detail.gcomponentCode || '칸';
    return (
      <FormControl
        fullWidth={!inline}
        size="small"
        key={detail.gcomponentId}
        disabled={locked || readOnly}
        sx={inline ? { width: fieldWidth } : undefined}
      >
        <InputLabel id={`folder-material-slot-${detail.gcomponentId}`}>{label}</InputLabel>
        <Select
          labelId={`folder-material-slot-${detail.gcomponentId}`}
          label={label}
          value={current}
          onChange={(e) => handleChange(detail, e.target.value)}
        >
          {shownItems.map((item) => (
            <MenuItem key={item.id} value={item.id}>{item.name || item.code}</MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  });

  if (inline) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
        {fields}
      </Box>
    );
  }

  return (
    <Grid item xs={12}>
      <Box sx={slotColumnSx}>
        {fields}
      </Box>
    </Grid>
  );
};

export default SubMatFolderMaterialSlotFields;
